/**
 * RoleAssignmentModal Component (DC-1094)
 *
 * Modal for assigning a governance role to an employee.
 * Used when Bridge AI recommends role assignments.
 *
 * Features:
 * - Display persona name and description
 * - Employee dropdown with search/filter
 * - Pre-select suggested employees if provided
 * - Loading state during API call
 * - ESC key + backdrop click to close
 *
 * @version 1.0.0
 * @date 2026-01-26
 */

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  useEmployees,
  usePersonas,
  assignRole,
} from './useGovernanceActions';
import type {
  RoleAssignmentModalProps,
} from './types';
import './RoleAssignmentModal.css';

export const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  journeyId,
  recommendationId,
  recommendationTitle,
  personaId,
  personaName,
  suggestedEmployeeIds,
}) => {
  const { employees, loading: loadingEmployees } = useEmployees();
  const { personas, loading: loadingPersonas } = usePersonas();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get persona details
  const persona = useMemo(() => {
    if (personaId) {
      return personas.find(p => p.persona_id === personaId || p.persona_label === personaId);
    }
    return null;
  }, [personas, personaId]);

  // Filter employees by search term
  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    const term = searchTerm.toLowerCase();
    return employees.filter(
      e => e.name.toLowerCase().includes(term) ||
           (e.email?.toLowerCase().includes(term)) ||
           (e.department?.toLowerCase().includes(term))
    );
  }, [employees, searchTerm]);

  // Pre-select suggested employee
  useEffect(() => {
    if (suggestedEmployeeIds && suggestedEmployeeIds.length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(suggestedEmployeeIds[0]);
    }
  }, [suggestedEmployeeIds, selectedEmployeeId]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, submitting, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !submitting) {
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedEmployeeId || !personaId) {
      setError('Please select an employee');
      return;
    }

    const selectedEmployee = employees.find(e => e.employee_id === selectedEmployeeId);

    try {
      setSubmitting(true);
      setError(null);

      const action = await assignRole({
        journey_id: journeyId,
        recommendation_id: recommendationId,
        recommendation_title: recommendationTitle,
        persona_id: personaId,
        persona_name: personaName || persona?.persona_name,
        employee_id: selectedEmployeeId,
        employee_name: selectedEmployee?.name,
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess(action);
        onClose();
      }, 1500);
    } catch (err: unknown) {
      console.error('[RoleAssignmentModal] Submit error:', err);
      setError(err instanceof Error ? err.message : 'Failed to assign role');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const loading = loadingEmployees || loadingPersonas;
  const displayPersonaName = personaName || persona?.persona_name || 'Unknown Role';

  return createPortal(
    <div
      className="modal-overlay role-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="role-assignment-title"
    >
      <div className="modal-container role-assignment-modal">
        <div className="modal-header">
          <h2 id="role-assignment-title" className="modal-title">
            Assign Role
          </h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Persona Info */}
            <div className="persona-info-section">
              <div className="persona-header">
                <span className="persona-icon">👤</span>
                <span className="persona-name">{displayPersonaName}</span>
              </div>
              {persona?.description && (
                <p className="persona-description">{persona.description}</p>
              )}
              {recommendationTitle && (
                <div className="recommendation-context">
                  <span className="context-label">From recommendation:</span>
                  <span className="context-value">{recommendationTitle}</span>
                </div>
              )}
            </div>

            {/* Employee Selection */}
            <div className="form-group">
              <label htmlFor="employee-search" className="form-label">
                Select Employee
              </label>

              <input
                id="employee-search"
                type="text"
                className="form-input search-input"
                placeholder="Search by name, email, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={loading || submitting}
              />

              <div className="employee-list">
                {loading ? (
                  <div className="loading-state">Loading employees...</div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="empty-state">No employees found</div>
                ) : (
                  filteredEmployees.map((employee) => (
                    <label
                      key={employee.employee_id}
                      className={`employee-option ${
                        selectedEmployeeId === employee.employee_id ? 'selected' : ''
                      } ${
                        suggestedEmployeeIds?.includes(employee.employee_id) ? 'suggested' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="employee"
                        value={employee.employee_id}
                        checked={selectedEmployeeId === employee.employee_id}
                        onChange={(e) => setSelectedEmployeeId(e.target.value)}
                        disabled={submitting}
                      />
                      <div className="employee-details">
                        <span className="employee-name">{employee.name}</span>
                        {employee.title && (
                          <span className="employee-title">{employee.title}</span>
                        )}
                        {employee.department && (
                          <span className="employee-department">{employee.department}</span>
                        )}
                        {employee.email && (
                          <span className="employee-email">{employee.email}</span>
                        )}
                      </div>
                      {suggestedEmployeeIds?.includes(employee.employee_id) && (
                        <span className="suggested-badge">Suggested</span>
                      )}
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message" role="alert">
                {error}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="success-message" role="status">
                Role assigned successfully!
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!selectedEmployeeId || submitting || loading || success}
            >
              {submitting ? 'Assigning...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default RoleAssignmentModal;
